import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router';
import { CHARACTER_KINDS, charactersApi, previewImage } from '../api/library.js';
import { EmptyState } from '../components/EmptyState.js';
import { useI18n } from '../i18n/context.js';
import { CharacterEditor } from '../library/CharacterEditor.js';
import { ActionError } from '../library/ActionError.js';
import { useActionFeedback } from '../library/useActionFeedback.js';
import { LibraryCard } from '../library/LibraryCard.js';
import { LibraryPicker } from '../library/LibraryPicker.js';
import { useCreatedItemScroll } from '../library/useCreatedItemScroll.js';

/** Библиотека героев `/app/characters` (01-SPEC.md §5.10). */
export function CharactersPage(): JSX.Element {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const bookId = searchParams.get('bookId');
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const action = useActionFeedback();
  const characters = useQuery({ queryKey: ['characters'], queryFn: () => charactersApi.list() });
  const { scrollToCreatedItem, itemRef } = useCreatedItemScroll<HTMLLIElement>(
    characters.data?.items.map((character) => character.id) ?? [],
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['characters'] });
  };

  if (characters.isPending) return <p>{t.common.loading}</p>;
  if (characters.isError) return <p role="alert">{t.common.unexpectedError}</p>;

  const items = characters.data.items;

  return (
    <section className="mx-auto grid max-w-7xl gap-5">
      <Link
        to={bookId ? `/app/books/${encodeURIComponent(bookId)}?step=characters` : '/app'}
        className="kitup-quiet-button w-fit"
      >
        ← {t.common.back}
      </Link>

      <div>
        <h1 className="text-2xl font-semibold">{t.characters.title}</h1>
      </div>

      <LibraryPicker
        addLabel={t.characters.add}
        createLabel={t.characters.create}
        nameLabel={t.characters.fields.name}
        kinds={{
          label: t.characters.fields.kind,
          options: CHARACTER_KINDS.map((kind) => ({
            value: kind,
            label: t.characters.kinds[kind],
          })),
        }}
        options={[]}
        onAdd={() => undefined}
        onCreate={(name, kind) =>
          charactersApi.create({ name, kind: kind! }).then((created) => {
            setOpenId(created.id);
            scrollToCreatedItem(created.id);
            refresh();
          })
        }
      />
      <ActionError message={action.error} />

      {items.length === 0 ? (
        <EmptyState title={t.characters.empty} hint={t.characters.emptyHint} />
      ) : null}

      <ul className="grid gap-3">
        {items.map((character) => (
          <LibraryCard
            key={character.id}
            elementRef={itemRef(character.id)}
            title={character.name}
            subtitle={t.characters.kinds[character.kind]}
            previewUrl={previewImage(character)}
            usage={character.usage}
            renditions={character.renditions}
            open={openId === character.id}
            blocked={character.usage.activeBooks > 0}
            onToggle={() => setOpenId(openId === character.id ? null : character.id)}
            onArchive={() => action.run(charactersApi.archive(character.id).then(refresh))}
            onDelete={() => action.run(charactersApi.remove(character.id).then(refresh))}
          >
            <CharacterEditor character={character} styleId={null} onChanged={refresh} />
          </LibraryCard>
        ))}
      </ul>
    </section>
  );
}
